
import { defineComponent, ref, onMounted, nextTick, watch } from 'vue';

export default defineComponent({
  name: 'Carousel',
  props: {
    carouselImages: Array
  },
  setup(props, { emit }) {
    const imgActive = ref<number>(0);
    const indicatorActive = ref<number>(0);
    const animating = ref<boolean>(false);
    const carouselImagesRef = ref<HTMLElement | null>(null);

    const images = ref([]);

    watch(
      () => props.carouselImages,
      (val) => {
        // @ts-ignore
        images.value = [...val];
      },
      {
        deep: true,
        immediate: true
      }
    );

    const clickImg = (img: { url: string }) => {
      emit('clickImg', img);
    };

    onMounted(() => {
      resolveCaroulselClass();
    });

    const resolveCaroulselClass = () => {
      const { prevElement, nextElement, afterNextElement, beforePrevElement } =
        getElements();
      prevElement?.classList.add('img-prev');
      nextElement?.classList.add('img-next');
      afterNextElement?.classList.add('after-next');
      beforePrevElement?.classList.add('before-prev');
    };

    const getElements = () => {
      const activeElement = carouselImagesRef.value?.querySelector('.active');

      let prevElement =
        activeElement?.previousElementSibling ??
        carouselImagesRef.value?.lastElementChild;
      let beforePrevElement =
        prevElement?.previousElementSibling ??
        carouselImagesRef.value?.lastElementChild;

      let nextElement =
        activeElement?.nextElementSibling ??
        carouselImagesRef.value?.firstElementChild;

      let afterNextElement =
        nextElement?.nextElementSibling ??
        carouselImagesRef.value?.firstElementChild;

      if (images.value.length === 1) {
        prevElement = null;
        nextElement = null;
        afterNextElement = null;
        beforePrevElement = null;
      }

      return {
        activeElement,
        prevElement,
        nextElement,
        afterNextElement,
        beforePrevElement
      };
    };

    const clearClassAfterAnimated = () => {
      carouselImagesRef.value
        ?.querySelectorAll('.carousel-image')
        .forEach((el) => {
          el.classList.remove(
            'img-prev',
            'img-next',
            'before-prev',
            'after-next',
            'slide-to-left',
            'slide-to-right'
          );
        });
    };

    const onPrev = () => {
      if (animating.value) {
        return;
      }
      animating.value = true;
      // set indicator
      if (indicatorActive.value === 0) {
        indicatorActive.value = images.value.length - 1;
      } else {
        indicatorActive.value--;
      }

      // append animation
      const { prevElement, activeElement, nextElement, beforePrevElement } =
        getElements();
      activeElement?.classList.add('slide-to-right');
      prevElement?.classList.add('slide-to-right');
      nextElement?.classList.add('slide-to-right');
      beforePrevElement?.classList.add('slide-to-right');

      setTimeout(() => {
        // remove animation class and init position class
        if (imgActive.value === 0) {
          imgActive.value = images.value.length - 1;
        } else {
          imgActive.value--;
        }
        // clear animation class
        clearClassAfterAnimated();

        nextTick(() => {
          resolveCaroulselClass();
        });

        animating.value = false;
      }, 800);
    };
    const onNext = () => {
      if (animating.value) {
        return;
      }
      animating.value = true;
      // set indicator
      if (indicatorActive.value === images.value.length - 1) {
        indicatorActive.value = 0;
      } else {
        indicatorActive.value++;
      }

      // append animation
      const { prevElement, activeElement, nextElement, afterNextElement } =
        getElements();
      activeElement?.classList.add('slide-to-left');
      prevElement?.classList.add('slide-to-left');
      nextElement?.classList.add('slide-to-left');
      afterNextElement?.classList.add('slide-to-left');

      setTimeout(() => {
        // remove animation class and init position class
        if (imgActive.value === images.value.length - 1) {
          imgActive.value = 0;
        } else {
          imgActive.value++;
        }
        // clear animation class
        clearClassAfterAnimated();
        nextTick(() => {
          resolveCaroulselClass();
        });

        animating.value = false;
      }, 800);
    };

    const selectIndicator = (index: number) => {
      if (animating.value) {
        return;
      }

      const currentIndicatorActive = indicatorActive.value;
      if (index === currentIndicatorActive) {
        return;
      }
      clearClassAfterAnimated();
      if (currentIndicatorActive <= index) {
        imgActive.value = index - 1;
        indicatorActive.value = index - 1;
      } else {
        imgActive.value = index + 1;
        indicatorActive.value = index + 1;
      }

      nextTick(() => {
        resolveCaroulselClass();
      });

      if (currentIndicatorActive <= index) {
        nextTick(() => {
          onNext();
        });
      } else {
        nextTick(() => {
          onPrev();
        });
      }
    };

    return {
      animating,
      carouselImagesRef,
      imgActive,
      indicatorActive,
      images,
      onPrev,
      onNext,
      selectIndicator,
      clickImg
    };
  }
});
